import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, imgUrl, money } from "../../lib/api";
import { toast } from "sonner";
import SafeImage from "../../components/common/SafeImage";
import {
  CheckCircle2, Clock, Package, Truck, MapPin, Home as HomeIcon,
  XCircle, RefreshCw, Receipt,
} from "lucide-react";

// Timeline step labels + icons in the order they should render
const STEPS = [
  { key: "placed",           label: "Placed",           icon: Receipt },
  { key: "confirmed",        label: "Confirmed",        icon: CheckCircle2 },
  { key: "processing",       label: "Processing",       icon: Clock },
  { key: "packed",           label: "Packed",           icon: Package },
  { key: "shipped",          label: "Shipped",          icon: Truck },
  { key: "out_for_delivery", label: "Out for Delivery", icon: MapPin },
  { key: "delivered",        label: "Delivered",        icon: HomeIcon },
];

const TERMINAL_STATES = new Set(["cancelled", "returned", "refunded"]);
const CUSTOMER_CANCELLABLE = new Set(["placed", "confirmed", "processing", "packed"]);

function statusLabel(s) {
  return (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function Timeline({ order }) {
  const history = order.status_history || [];
  const historyByStatus = Object.fromEntries(history.map((h) => [h.status, h]));
  const currentStep = STEPS.findIndex((s) => s.key === order.status);

  if (TERMINAL_STATES.has(order.status)) {
    return (
      <div className="border border-[hsl(var(--kb-border))] p-5" data-testid="order-timeline">
        <div className="overline mb-3">Order timeline</div>
        {history.map((h, i) => (
          <div key={i} className="flex gap-3 pb-3 text-sm">
            <div className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${h.status === order.status ? "bg-[hsl(var(--kb-primary))]" : "bg-[hsl(var(--kb-border))]"}`} />
            <div className="flex-1">
              <div className="font-medium">{statusLabel(h.status)}</div>
              <div className="text-xs text-[hsl(var(--kb-muted))]">{new Date(h.at).toLocaleString()}</div>
              {h.note && <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5">{h.note}</div>}
            </div>
          </div>
        ))}
        <div className={`mt-2 inline-block text-xs uppercase tracking-widest px-3 py-1 ${order.status === "cancelled" ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-800"}`}>
          Order {statusLabel(order.status)}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[hsl(var(--kb-border))] p-5" data-testid="order-timeline">
      <div className="overline mb-4">Order timeline</div>
      <div className="relative">
        <div className="absolute top-4 left-0 right-0 h-px bg-[hsl(var(--kb-border))]" />
        <div className="grid grid-cols-7 relative">
          {STEPS.map((step, i) => {
            const entry = historyByStatus[step.key];
            const isDone = currentStep >= i;
            const isCurrent = currentStep === i;
            const Icon = step.icon;
            return (
              <div key={step.key} className="flex flex-col items-center gap-2 relative" data-testid={`timeline-step-${step.key}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white z-10 ${
                    isCurrent
                      ? "border-[hsl(var(--kb-primary))] text-[hsl(var(--kb-primary))]"
                      : isDone
                      ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))] text-white"
                      : "border-[hsl(var(--kb-border))] text-[hsl(var(--kb-muted))]"
                  }`}
                >
                  {isDone && !isCurrent ? <CheckCircle2 size={14} /> : <Icon size={14} />}
                </div>
                <div className={`text-[10px] sm:text-xs text-center leading-tight ${isDone ? "text-[hsl(var(--kb-primary))] font-medium" : "text-[hsl(var(--kb-muted))]"}`}>
                  {step.label}
                </div>
                {entry && (
                  <div className="text-[9px] text-[hsl(var(--kb-muted))] hidden sm:block">
                    {new Date(entry.at).toLocaleDateString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundForm, setRefundForm] = useState({ reason: "Product damaged", note: "" });
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load order");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/orders/${id}/cancel`, { reason: cancelReason.trim() || undefined });
      toast.success("Order cancelled");
      setCancelOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  const doRefund = async (e) => {
    e.preventDefault();
    if (!refundForm.reason.trim()) return toast.error("Please add a reason");
    setRefundSubmitting(true);
    try {
      await api.post(`/orders/mine/refunds`, {
        order_id: id,
        reason: refundForm.reason.trim(),
        note: refundForm.note.trim() || undefined,
      });
      toast.success("Refund request submitted");
      setRefundOpen(false);
      setRefundForm({ reason: "Product damaged", note: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit refund");
    } finally {
      setRefundSubmitting(false);
    }
  };

  if (loading) return <div className="px-6 py-24 text-center">Loading order…</div>;
  if (error) return <div className="px-6 py-24 text-center text-red-700">{error}</div>;
  if (!order) return null;

  const canCancel = CUSTOMER_CANCELLABLE.has(order.status);
  const canRequestRefund =
    ["cancelled", "returned"].includes(order.status) &&
    (order.payment_status === "paid");

  return (
    <div className="px-6 md:px-12 lg:px-24 py-10 max-w-5xl mx-auto" data-testid="order-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-widest mb-6 text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
      >
        ← Back
      </button>

      <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
        <div>
          <div className="overline mb-2">Order #{(order.order_number || order.id).slice(0, 18)}</div>
          <h1 className="font-display text-3xl lg:text-4xl">{statusLabel(order.status)}</h1>
          <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">
            Placed {new Date(order.created_at).toLocaleString()}
            {order.payment_method && <> · Payment: {order.payment_method.toUpperCase()}</>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canCancel && (
            <button
              className="kb-btn-outline flex items-center gap-1"
              onClick={() => setCancelOpen(true)}
              data-testid="cancel-order-btn"
            >
              <XCircle size={14} /> Cancel Order
            </button>
          )}
          {canRequestRefund && (
            <button
              className="kb-btn-outline flex items-center gap-1"
              onClick={() => setRefundOpen(true)}
              data-testid="refund-request-btn"
            >
              <RefreshCw size={14} /> Request Refund
            </button>
          )}
        </div>
      </div>

      <Timeline order={order} />

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <div className="md:col-span-2 space-y-4">
          <div className="overline">Items · {(order.items || []).length}</div>
          {(order.items || []).map((it, i) => (
            <div key={i} className="flex gap-4 border border-[hsl(var(--kb-border))] p-4" data-testid={`order-item-${i}`}>
              <Link to={`/product/${it.slug}`} className="w-20 aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] shrink-0 overflow-hidden">
                <SafeImage src={imgUrl(it.image)} alt="" className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${it.slug}`} className="font-medium line-clamp-2">{it.title}</Link>
                <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                  Qty {it.qty}{it.size ? ` · ${it.size}` : ""}
                </div>
                <div className="text-sm font-medium mt-2">{money((it.price || 0) * it.qty)}</div>
              </div>
            </div>
          ))}

          {(order.seller_splits || []).length > 1 && (
            <div className="border border-[hsl(var(--kb-border))] p-4" data-testid="seller-splits">
              <div className="overline mb-3">Multi-seller order · Split summary</div>
              <div className="space-y-2 text-sm">
                {order.seller_splits.map((s, i) => (
                  <div key={i} className="flex justify-between gap-3" data-testid={`split-${i}`}>
                    <span className="text-[hsl(var(--kb-muted))] truncate mr-2">
                      {s.shop_name || `Seller · ${(s.seller_id || "").slice(0, 10)}`}
                    </span>
                    <span className="shrink-0">{money(s.total)} ({s.items.length} item{s.items.length === 1 ? "" : "s"})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="border border-[hsl(var(--kb-border))] p-4">
            <div className="overline mb-3">Summary</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              {order.discount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}</span>
                  <span>−{money(order.discount)}</span>
                </div>
              )}
              <div className="flex justify-between"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : money(order.shipping)}</span></div>
              <div className="flex justify-between font-display text-lg pt-2 border-t border-[hsl(var(--kb-border))] mt-2"><span>Total</span><span data-testid="order-total">{money(order.total)}</span></div>
            </div>
          </div>

          {order.address && (
            <div className="border border-[hsl(var(--kb-border))] p-4">
              <div className="overline mb-2">Delivery to</div>
              <p className="text-sm">
                <b>{order.address.name}</b><br />
                {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}<br />
                {order.address.city}, {order.address.state} {order.address.pincode}<br />
                <span className="text-xs text-[hsl(var(--kb-muted))]">{order.address.phone}</span>
              </p>
            </div>
          )}
        </aside>
      </div>

      {cancelOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCancelOpen(false)} data-testid="cancel-modal">
          <div className="bg-white max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="overline">Cancel Order</div>
            <h2 className="font-display text-2xl">Are you sure?</h2>
            <p className="text-sm text-[hsl(var(--kb-muted))]">
              This will cancel the entire order. Online payments will be refunded within 5-7 business days.
            </p>
            <textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason (optional)"
              className="w-full border border-[hsl(var(--kb-border))] p-3 text-sm"
              data-testid="cancel-reason"
            />
            <div className="flex gap-3">
              <button className="kb-btn-outline flex-1" onClick={() => setCancelOpen(false)} disabled={cancelling}>Keep Order</button>
              <button className="kb-btn-primary flex-1" onClick={doCancel} disabled={cancelling} data-testid="cancel-confirm-btn">
                {cancelling ? "Cancelling…" : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {refundOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRefundOpen(false)} data-testid="refund-modal">
          <form onSubmit={doRefund} className="bg-white max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="overline">Refund Request</div>
            <h2 className="font-display text-2xl">Get your money back</h2>
            <p className="text-sm text-[hsl(var(--kb-muted))]">Full refund of <b>{money(order.total)}</b> will be processed within 5-7 business days.</p>
            <select
              value={refundForm.reason}
              onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
              className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm"
              data-testid="refund-reason"
            >
              <option>Product damaged</option>
              <option>Not as described</option>
              <option>Wrong item</option>
              <option>Late delivery</option>
              <option>Changed my mind</option>
              <option>Other</option>
            </select>
            <textarea
              rows={3}
              value={refundForm.note}
              onChange={(e) => setRefundForm({ ...refundForm, note: e.target.value })}
              placeholder="Additional notes (optional)"
              className="w-full border border-[hsl(var(--kb-border))] p-3 text-sm"
            />
            <div className="flex gap-3">
              <button type="button" className="kb-btn-outline flex-1" onClick={() => setRefundOpen(false)} disabled={refundSubmitting}>Cancel</button>
              <button className="kb-btn-primary flex-1" disabled={refundSubmitting} data-testid="refund-submit-btn">
                {refundSubmitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
